import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  agentAnalyticsColumnDefinitions,
  calculateAgentAnalyticsTotals,
  sortAgentAnalyticsData,
  getPerformanceColor,
  formatPercentage
} from './agent-table-data';

export const AgentAnalyticsTable = ({
  title = "Agent Performance Analytics",
  data = [],
  loading = false,
  error = null,
  fromDate,
  toDate,
  sortable = true,
  showTotals = true,
  elevation = 2
}) => {
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('attemptPercentage');

  const handleRequestSort = (property) => {
    if (!sortable) return;

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle loading state
  if (loading) {
    return (
      <Paper elevation={elevation} sx={{ p: 3, border: "1px solid #d0d0d0" }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>Loading agent analytics...</Typography>
        </Box>
      </Paper>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Paper elevation={elevation} sx={{ p: 3, border: "1px solid #d0d0d0" }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Alert severity="error">
          Failed to load agent analytics: {error.message || 'Unknown error'}
        </Alert>
      </Paper>
    );
  }

  // Helper to format range
  const formatRange = () => {
    try {
      const from = fromDate ? dayjs(fromDate).format('DD MMM YYYY') : null;
      const to = toDate ? dayjs(toDate).format('DD MMM YYYY') : null;
      if (from && to) return `${from} - ${to}`;
      if (from) return `${from} - ?`;
      if (to) return `? - ${to}`;
      return 'the selected period';
    } catch (e) {
      return 'the selected period';
    }
  };

  // Handle empty data
  if (!data || data.length === 0) {
    return (
      <Paper elevation={elevation} sx={{ p: 3, border: "1px solid #d0d0d0" }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Alert severity="info">
          No agent data available for {formatRange()}.
        </Alert>
      </Paper>
    );
  }

  const sortedData = sortable ? sortAgentAnalyticsData(data, orderBy, order) : data;
  const totalsRow = showTotals ? calculateAgentAnalyticsTotals(data) : null;

  return (
    <Paper elevation={elevation} sx={{ p: 3, border: "1px solid #d0d0d0" }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {title}
        {(fromDate || toDate) && (
          <Typography variant="subtitle2" color="text.secondary" component="span" sx={{ ml: 1 }}>
            ({formatRange()})
          </Typography>
        )}
      </Typography>

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small" sx={{ minWidth: 1200 }}>
          <TableHead >
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {agentAnalyticsColumnDefinitions.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                    width: column.width,
                    minWidth: column.id === 'agentName' ? 150 : 80,
                    position: column.sticky ? 'sticky' : 'sticky',
                    left: column.id === 'agentName' ? 0 : 'auto',
                    // top: 0,
                    zIndex: column.sticky ? 3 : 1,
                    borderRight: column.sticky ? '2px solid #e0e0e0' : 'none'
                  }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {sortable && column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                      {orderBy === column.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((agent, index) => (
              <TableRow
                key={`${agent.agentName}-${index}`}
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                  '&:hover': { backgroundColor: '#e3f2fd' }
                }}
              >
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: 'medium',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'white',
                    zIndex: 2,
                    borderRight: '2px solid #e0e0e0'
                  }}
                >
                  {agent.agentName || 'Unknown Agent'}
                </TableCell>
                <TableCell align="right">{agent.allocated?.toLocaleString() || '0'}</TableCell>
                <TableCell align="right">{agent.totalAttempted?.toLocaleString() || '0'}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={formatPercentage(agent.attemptPercentage)}
                    size="small"
                    color={getPerformanceColor(agent.attemptPercentage || 0)}
                    variant="filled"
                    sx={{ minWidth: 60, fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell align="right">{agent.connected?.toLocaleString() || '0'}</TableCell>
                <TableCell align="right">{agent.unreached?.toLocaleString() || '0'}</TableCell>
                <TableCell align="right">{agent.notInThePortal?.toLocaleString() || '0'}</TableCell>
                <TableCell align="right">{agent.childAbove2Years?.toLocaleString() || '0'}</TableCell>
                <TableCell align="right">{agent.active?.toLocaleString() || '0'}</TableCell>
                <TableCell align="right">{agent.pending?.toLocaleString() || '0'}</TableCell>
                <TableCell align="right">{agent.unattempted?.toLocaleString() || '0'}</TableCell>
              </TableRow>
            ))}

            {/* Totals Row */}
            {totalsRow && (
              <TableRow sx={{
                backgroundColor: '#e8f5e8',
                '&:hover': { backgroundColor: '#d4edda' },
                borderTop: '2px solid #28a745'
              }}>
                <TableCell
                  align="left"
                  sx={{
                    fontWeight: 'bold',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#e8f5e8',
                    zIndex: 2,
                    borderRight: '2px solid #e0e0e0'
                  }}
                >
                  {totalsRow.agentName}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.allocated.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.totalAttempted.toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={formatPercentage(totalsRow.attemptPercentage)}
                    size="small"
                    color={getPerformanceColor(totalsRow.attemptPercentage)}
                    variant="filled"
                    sx={{ minWidth: 60, fontWeight: 'bold' }}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.connected.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.unreached.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.notInThePortal.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.childAbove2Years.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.active.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.pending.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {totalsRow.unattempted.toLocaleString()}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>      {/* Summary Information */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Agents:</strong> {data.length} agents |
          <strong> Total Records:</strong> {totalsRow ? totalsRow.allocated.toLocaleString() : 'N/A'} |
          <strong> Overall Performance:</strong> {totalsRow ? formatPercentage(totalsRow.attemptPercentage) : 'N/A'} |
          <strong> Sorting:</strong> {sortable ? 'Enabled' : 'Disabled'}
          {orderBy && sortable && (
            <span> | <strong>Current Sort:</strong> {agentAnalyticsColumnDefinitions.find(col => col.id === orderBy)?.label} ({order})</span>
          )}
        </Typography>
      </Box>
    </Paper>
  );
};
